import { useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import { FallbackComponent } from "components/Error/Fallback";

import { useAuthStore } from "@type/store";
import { useTranslationContext } from "context/TranslationContext";
import { getBackendErrorMessage } from "helpers/getBackendErrorMessage";
import { logError } from "lib/sentry/logError";

interface ToastDataProps {
  message?: string;
  reffId?: string;
  error?: any;
}

const ToastData = ({ message, error, reffId }: ToastDataProps) => {
  const { t } = useTranslationContext();
  const user = useAuthStore((state) => state.user);
  const requestedRef = useRef(false);

  const uuid = useMemo(() => uuidv4(), []);

  const parsedRefId = useMemo(() => {
    let tmpReffId = "";

    if (reffId) {
      tmpReffId = reffId;
    } else if (error?.data) {
      let objError = error?.data;
      if (typeof error?.data === "string") {
        try {
          objError = JSON.parse(error?.data);
        } catch {
          objError = undefined;
        }
      }
      tmpReffId = objError?.ref_id || "";
    }

    return tmpReffId?.toUpperCase()?.replace(/[()]/g, "");
  }, [reffId, error]);

  const finalMessage = useMemo(() => {
    if (!error) return message || "";

    const { errorMessage, requiredTranslate } = getBackendErrorMessage(
      error,
      t(message || "")
    );

    return requiredTranslate ? t(errorMessage) : errorMessage;
  }, [error, message, t]);

  useEffect(() => {
    if (error && !requestedRef.current) {
      logError({
        traceId: uuid,
        user: user,
        error: error,
      });

      requestedRef.current = true;
    }
  }, [uuid, user, error]);

  if (error) {
    return (
      <div className="lg:max-w-[35vw]">
        <FallbackComponent
          traceId={uuid}
          refId={parsedRefId}
          size="md"
          hideAction
          customMessage={finalMessage}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && <div className="w-full font-semibold">{parsedRefId}</div>}
      <div className="w-full">{finalMessage}</div>
    </div>
  );
};

export default ToastData;
